import dayjs from "dayjs";

import type { ColumnType } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip, TruncatedTextWithTooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { getColumnSorter } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/prompts-table/utils";
import type { IModifiablePrompt } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/types";
import type { SortDirection } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { StyledIconButton } from "pro-features/components/Rule/src/styledComponents";

import { TopicsDropdown } from "./TopicsDropdown";

export const getColumnConfig = (
    onEdit: (prompt: IModifiablePrompt, topic: string) => void,
    onDelete: (prompt: IModifiablePrompt) => void,
    sorting: { dataIndex: keyof IModifiablePrompt; sortDirection: SortDirection },
    allTopics: string[],
    totalPromptsCount: number,
): Array<ColumnType<IModifiablePrompt>> => [
    {
        dataIndex: "query",
        title: translate("llm.prompts.edit.prompts-table.column.prompt.title"),
        tooltipSubtitle: translate("llm.prompts.edit.prompts-table.column.prompt.tooltip"),
        sortOrder: sorting.dataIndex === "query" ? sorting.sortDirection : undefined,
        sorter: getColumnSorter("query"),
        width: 500,
        render: (text: string) => (
            <TruncatedTextWithTooltip
                fontVariant="input2"
                color={colorsPalettes.carbon[400]}
            >
                {text}
            </TruncatedTextWithTooltip>
        ),
    },
    {
        dataIndex: "currentTopicName",
        title: translate("llm.prompts.edit.prompts-table.column.topic.title"),
        tooltipSubtitle: translate("llm.prompts.edit.prompts-table.column.topic.tooltip"),
        sortOrder: sorting.dataIndex === "currentTopicName" ? sorting.sortDirection : undefined,
        sorter: getColumnSorter("currentTopicName"),
        width: 100,
        render: (_, row: IModifiablePrompt) => (
            <TopicsDropdown
                prompt={row}
                allTopics={allTopics}
                onEdit={onEdit}
            />
        ),
    },
    {
        dataIndex: "timestamp",
        title: translate("llm.prompts.edit.prompts-table.column.last-update.title"),
        tooltipSubtitle: translate("llm.prompts.edit.prompts-table.column.last-update.tooltip"),
        sortOrder: sorting.dataIndex === "timestamp" ? sorting.sortDirection : undefined,
        sorter: getColumnSorter("timestamp"),
        width: 100,
        render: (timestamp: string) => dayjs.utc(timestamp).fromNow(),
    },
    {
        width: 40,
        render: (_, row: IModifiablePrompt) => (
            <Flex
                alignItems="center"
                justifyContent="center"
            >
                <Tooltip content={translate("global.delete")}>
                    <StyledIconButton
                        type="flat"
                        iconName="delete"
                        iconSize="xs"
                        isDisabled={totalPromptsCount <= 1}
                        onClick={() => onDelete(row)}
                    />
                </Tooltip>
            </Flex>
        ),
    },
];
